import React, { useEffect, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  X,
  Settings2,
  RefreshCw,
  Globe,
  Workflow,
  Database,
  BarChart3,
  PieChart,
  FileText,
  TrendingUp,
  Users,
  MessageSquare,
  FileBarChart,
  LineChart,
  Table,
  ClipboardList,
  Activity,
  Plus,
  Trash2,
} from 'lucide-react';
import FilterableMultiSelect from '../../../common/FilterableMultiSelect';
import { apiService } from '../../../../../components/api/apiService';
import {
  analyticsHubService,
  mapAnalyticsHubChatAgentOption,
} from '../../../../../components/api/analyticsHubService';
import {
  fetchAvailableWorkflows,
  workflowApiErrorMessage,
  type AvailableWorkflowDto,
} from '../../../workflowbuilder/postWorkflowService';
import type { Theme, ThemeColors, ReportParameter, ReportSettings, SourceType } from './types';
import { generateReportId, UNIVERSAL_REPORT_ID } from './utils';
import {
  applyParameterMappingTypeChange,
  applyParameterNameChange,
  CLAIM_PRESETS,
  createEmptyReportParameter,
  CUSTOM_CLAIM_OPTION,
  CUSTOM_PARAMETER_VALUE,
  normalizeReportParameter,
} from './reportParameters';

const THEME_CONFIG: Record<Theme, ThemeColors> = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: { primary: 'text-white', secondary: 'text-neutral-300', muted: 'text-neutral-400' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-neutral-700',
    input: 'bg-neutral-800 border-neutral-600 text-white placeholder-neutral-500',
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: { primary: 'text-gray-900', secondary: 'text-gray-700', muted: 'text-gray-500' },
    accent: 'bg-indigo-600',
    hover: 'hover:bg-gray-100',
    input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-400',
  },
};

export const REPORT_ICON_OPTIONS: { value: string; label: string; Icon: LucideIcon }[] = [
  { value: 'BarChart3', label: 'Bar Chart', Icon: BarChart3 },
  { value: 'PieChart', label: 'Pie Chart', Icon: PieChart },
  { value: 'LineChart', label: 'Line Chart', Icon: LineChart },
  { value: 'FileText', label: 'Document', Icon: FileText },
  { value: 'FileBarChart', label: 'Report', Icon: FileBarChart },
  { value: 'TrendingUp', label: 'Trends', Icon: TrendingUp },
  { value: 'Users', label: 'Users', Icon: Users },
  { value: 'MessageSquare', label: 'Messages', Icon: MessageSquare },
  { value: 'Table', label: 'Table', Icon: Table },
  { value: 'ClipboardList', label: 'Checklist', Icon: ClipboardList },
  { value: 'Activity', label: 'Activity', Icon: Activity },
  { value: 'Database', label: 'Data', Icon: Database },
];

interface DataModelOption {
  id: string;
  name: string;
  description?: string;
}

interface ReportSettingsModalProps {
  theme?: Theme;
  settings: ReportSettings;
  onClose: () => void;
  onSave: (settings: ReportSettings) => void | Promise<void>;
  primaryActionLabel?: string;
  saving?: boolean;
  subtitle?: string;
}

const defaultSettings = (): ReportSettings => ({
  reportId: generateReportId(),
  isUniversalId: false,
  reportType: 'standalone',
  description: '',
  icon: 'BarChart3',
  isActive: true,
  ispublished: false,
  sourceType: 'workflow',
  workflowId: '',
  dataModelId: '',
  parameters: [],
  copilotIds: [],
  reportHeader: { title: '', subtitle: '' },
});

export { defaultSettings as createDefaultReportSettings };

interface ReportParametersEditorProps {
  parameters: ReportParameter[];
  onChange: (parameters: ReportParameter[]) => void;
  colors: ThemeColors;
  inputCls: string;
  labelCls: string;
}

const ReportParametersEditor: React.FC<ReportParametersEditorProps> = ({
  parameters,
  onChange,
  colors,
  inputCls,
  labelCls,
}) => {
  const [customClaimByIndex, setCustomClaimByIndex] = useState<Record<number, boolean>>({});

  const updateParameter = (index: number, next: ReportParameter) => {
    onChange(parameters.map((param, i) => (i === index ? next : param)));
  };

  const addParameter = () => {
    onChange([...parameters, createEmptyReportParameter()]);
  };

  const removeParameter = (index: number) => {
    onChange(parameters.filter((_, i) => i !== index));
    setCustomClaimByIndex((prev) => {
      const next: Record<number, boolean> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const idx = Number(key);
        if (idx < index) next[idx] = value;
        if (idx > index) next[idx - 1] = value;
      });
      return next;
    });
  };

  const isPresetClaim = (value: string) =>
    CLAIM_PRESETS.includes(value as (typeof CLAIM_PRESETS)[number]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className={`text-[10px] font-medium uppercase tracking-wide ${colors.text.muted}`}>
          Parameters
        </p>
        <button
          type="button"
          onClick={addParameter}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-medium ${colors.border} ${colors.hover} text-indigo-400`}
        >
          <Plus className="w-3 h-3" />
          Add
        </button>
      </div>

      {parameters.length === 0 && (
        <p className={`text-[10px] ${colors.text.muted} px-2 py-2 rounded-md border border-dashed ${colors.border}`}>
          No parameters configured. Add parameters for roles, users, claims, and other runtime values.
        </p>
      )}

      {parameters.map((param, index) => {
        const useCustomClaim =
          customClaimByIndex[index] ??
          (param.mapping_type === 'inherit_from_claims' && !!param.value && !isPresetClaim(param.value));

        return (
          <div
            key={`param-${index}`}
            className={`rounded-md border ${colors.border} p-2.5 space-y-2 ${colors.secondary}`}
          >
            <div className="flex items-start justify-between gap-2">
              <p className={`text-[10px] font-medium ${colors.text.secondary}`}>
                Parameter {index + 1}
              </p>
              <button
                type="button"
                onClick={() => removeParameter(index)}
                className="p-1 rounded hover:bg-red-500/15 text-neutral-400 hover:text-red-400"
                aria-label={`Remove parameter ${index + 1}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={labelCls}>Name</label>
                <input
                  type="text"
                  value={param.name}
                  onChange={(e) =>
                    updateParameter(index, applyParameterNameChange(param, e.target.value))
                  }
                  className={inputCls}
                  placeholder="e.g. roles, users, denied"
                />
              </div>

              <div>
                <label className={labelCls}>Mapping Type</label>
                <select
                  value={param.mapping_type}
                  onChange={(e) =>
                    updateParameter(
                      index,
                      applyParameterMappingTypeChange(
                        param,
                        e.target.value as ReportParameter['mapping_type']
                      )
                    )
                  }
                  className={inputCls}
                >
                  <option value="custom">Custom</option>
                  <option value="inherit_from_claims">Inherit from claims</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <label className={labelCls}>Value</label>
                {param.mapping_type === 'custom' ? (
                  <input
                    type="text"
                    value={CUSTOM_PARAMETER_VALUE}
                    disabled
                    className={`${inputCls} opacity-70 cursor-not-allowed`}
                    title="Custom mapping uses the default value"
                  />
                ) : useCustomClaim ? (
                  <input
                    type="text"
                    value={param.value}
                    onChange={(e) => updateParameter(index, { ...param, value: e.target.value })}
                    className={inputCls}
                    placeholder="Custom claim key, e.g. employee_id"
                  />
                ) : (
                  <select
                    value={param.value}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === CUSTOM_CLAIM_OPTION) {
                        setCustomClaimByIndex((prev) => ({ ...prev, [index]: true }));
                        updateParameter(index, { ...param, value: '' });
                        return;
                      }
                      updateParameter(index, { ...param, value });
                    }}
                    className={inputCls}
                  >
                    {CLAIM_PRESETS.map((claim) => (
                      <option key={claim} value={claim}>
                        {claim}
                      </option>
                    ))}
                    <option value={CUSTOM_CLAIM_OPTION}>Add custom claim…</option>
                  </select>
                )}
                {param.mapping_type === 'inherit_from_claims' && useCustomClaim && (
                  <button
                    type="button"
                    onClick={() => {
                      setCustomClaimByIndex((prev) => ({ ...prev, [index]: false }));
                      updateParameter(index, { ...param, value: CLAIM_PRESETS[0] });
                    }}
                    className="mt-1 text-[10px] text-indigo-400 hover:text-indigo-300"
                  >
                    Use preset claim
                  </button>
                )}
              </div>

              <label className={`flex items-center gap-1.5 pb-1.5 text-[10px] ${colors.text.secondary} whitespace-nowrap`}>
                <input
                  type="checkbox"
                  checked={param.isrequired}
                  onChange={(e) =>
                    updateParameter(index, { ...param, isrequired: e.target.checked })
                  }
                  className="rounded border-neutral-600"
                />
                Required
              </label>
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface SettingToggleRowProps {
  title: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  colors: ThemeColors;
  onClassName?: string;
}

const SettingToggleRow: React.FC<SettingToggleRowProps> = ({
  title,
  description,
  checked,
  onChange,
  colors,
  onClassName = 'bg-indigo-600',
}) => (
  <div
    className={`flex items-center justify-between gap-3 py-2 px-2.5 rounded-md border ${colors.border}`}
  >
    <div className="min-w-0 flex-1">
      <p className={`text-[11px] font-medium ${colors.text.primary}`}>{title}</p>
      <p className={`text-[10px] ${colors.text.muted}`}>{description}</p>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={title}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 ease-out ${
        checked ? onClassName : 'bg-neutral-600'
      }`}
    >
      <span
        aria-hidden
        className={`pointer-events-none absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ease-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

const ReportSettingsModal: React.FC<ReportSettingsModalProps> = ({
  theme = 'dark',
  settings,
  onClose,
  onSave,
  primaryActionLabel = 'Apply Settings',
  saving = false,
  subtitle = 'Configure metadata, access & data source',
}) => {
  const colors = THEME_CONFIG[theme];
  const [form, setForm] = useState<ReportSettings>(settings);
  const [headerJson, setHeaderJson] = useState(() => JSON.stringify(settings.reportHeader ?? {}, null, 2));
  const [headerError, setHeaderError] = useState<string | null>(null);
  const [parameterError, setParameterError] = useState<string | null>(null);

  const [copilotOptions, setCopilotOptions] = useState<{ label: string; value: string }[]>([]);
  const [loadingCopilots, setLoadingCopilots] = useState(false);

  const [workflows, setWorkflows] = useState<AvailableWorkflowDto[]>([]);
  const [dataModels, setDataModels] = useState<DataModelOption[]>([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(false);
  const [loadingDataModels, setLoadingDataModels] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    const loadCopilots = async () => {
      setLoadingCopilots(true);
      try {
        const response = await analyticsHubService.getChatAgents();
        const agents = response?.data ?? [];
        if (!cancelled) {
          setCopilotOptions(
            agents
              .map(mapAnalyticsHubChatAgentOption)
              .filter((opt): opt is { label: string; value: string } => opt !== null)
          );
        }
      } catch (err) {
        console.error('Failed to load copilots', err);
        if (!cancelled) setCopilotOptions([]);
      } finally {
        if (!cancelled) setLoadingCopilots(false);
      }
    };
    void loadCopilots();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (form.sourceType !== 'workflow') return;
    let cancelled = false;
    const load = async () => {
      setLoadingWorkflows(true);
      setLoadError(null);
      try {
        const data = await fetchAvailableWorkflows(true);
        if (!cancelled) setWorkflows(data);
      } catch (e) {
        if (!cancelled) {
          setLoadError(workflowApiErrorMessage(e));
          setWorkflows([]);
        }
      } finally {
        if (!cancelled) setLoadingWorkflows(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [form.sourceType]);

  useEffect(() => {
    if (form.sourceType !== 'datamodel') return;
    let cancelled = false;
    const load = async () => {
      setLoadingDataModels(true);
      setLoadError(null);
      try {
        const data = await apiService.get('/DataModels', {}, 'backend');
        const list = data?.dataModels ?? [];
        if (!cancelled) {
          setDataModels(
            list.map((item: { id: string; modelName: string; description?: string }) => ({
              id: item.id,
              name: item.modelName,
              description: item.description,
            }))
          );
        }
      } catch (err) {
        console.error('Failed to load data models:', err);
        if (!cancelled) {
          setLoadError('Failed to load data models');
          setDataModels([]);
        }
      } finally {
        if (!cancelled) setLoadingDataModels(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [form.sourceType]);

  const patch = (partial: Partial<ReportSettings>) =>
    setForm((prev) => ({ ...prev, ...partial }));

  const handleGenerateId = () => {
    patch({ reportId: generateReportId(), isUniversalId: false });
  };

  const handleSetUniversal = () => {
    patch({ reportId: UNIVERSAL_REPORT_ID, isUniversalId: true });
  };

  const handleSubmit = async () => {
    let reportHeader: Record<string, unknown>;
    try {
      reportHeader = headerJson.trim() ? JSON.parse(headerJson) : {};
      if (typeof reportHeader !== 'object' || reportHeader === null || Array.isArray(reportHeader)) {
        setHeaderError('Report header must be a JSON object');
        return;
      }
    } catch {
      setHeaderError('Invalid JSON in report header');
      return;
    }

    setHeaderError(null);
    setParameterError(null);

    const normalizedParameters = form.parameters.map((param) => normalizeReportParameter(param));
    const invalidParameter = normalizedParameters.find((param) => {
      if (!param.name.trim()) return true;
      if (param.mapping_type === 'inherit_from_claims' && !param.value.trim()) return true;
      return false;
    });
    if (invalidParameter) {
      setParameterError('Each parameter must have a name. Claim-based parameters also require a value.');
      return;
    }

    await onSave({ ...form, parameters: normalizedParameters, reportHeader });
  };

  const inputCls = `w-full px-2.5 py-1.5 rounded-md border text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500/50 ${colors.input}`;
  const labelCls = `block text-[10px] font-medium uppercase tracking-wide ${colors.text.muted} mb-1`;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-2xl max-h-[90vh] flex flex-col rounded-lg border ${colors.border} ${colors.surface} shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex-shrink-0 px-4 py-2.5 border-b ${colors.border} flex items-center justify-between`}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-indigo-600/20 flex items-center justify-center">
              <Settings2 className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <div>
              <h3 className={`text-xs font-semibold ${colors.text.primary}`}>Report Settings</h3>
              <p className={`text-[10px] ${colors.text.muted}`}>{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-1 rounded ${colors.hover} ${colors.text.muted}`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {/* Report ID */}
          <div>
            <label className={labelCls}>Report ID</label>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={form.reportId}
                onChange={(e) => patch({ reportId: e.target.value, isUniversalId: e.target.value === UNIVERSAL_REPORT_ID })}
                className={`flex-1 ${inputCls} font-mono`}
                placeholder="UUID"
              />
              <button
                type="button"
                onClick={handleGenerateId}
                title="Generate new UUID"
                className={`px-2 py-1.5 rounded-md border ${colors.border} ${colors.hover} ${colors.text.secondary} text-[10px] flex items-center gap-1 whitespace-nowrap`}
              >
                <RefreshCw className="w-3 h-3" />
                Generate
              </button>
              <button
                type="button"
                onClick={handleSetUniversal}
                title="Set universal report ID"
                className={`px-2 py-1.5 rounded-md border text-[10px] flex items-center gap-1 whitespace-nowrap transition-colors ${
                  form.isUniversalId
                    ? 'border-indigo-500/50 bg-indigo-600/20 text-indigo-400'
                    : `${colors.border} ${colors.hover} ${colors.text.secondary}`
                }`}
              >
                <Globe className="w-3 h-3" />
                Universal
              </button>
            </div>
            {form.isUniversalId && (
              <p className="text-[10px] text-indigo-400 mt-1">Universal ID — accessible across all tenants/agents</p>
            )}
          </div>

          {/* Report Type */}
          <div>
            <label className={labelCls}>Report Type</label>
            <select
              value={form.reportType}
              onChange={(e) => patch({ reportType: e.target.value as ReportSettings['reportType'] })}
              className={inputCls}
            >
              <option value="chatbot">ChatBot</option>
              <option value="standalone">StandAlone</option>
            </select>
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => patch({ description: e.target.value })}
              rows={2}
              className={`${inputCls} resize-none`}
              placeholder="Brief description of this report..."
            />
          </div>

          {/* Report Header */}
          <div>
            <label className={labelCls}>Report Header</label>
            <textarea
              value={headerJson}
              onChange={(e) => {
                setHeaderJson(e.target.value);
                setHeaderError(null);
              }}
              rows={4}
              spellCheck={false}
              className={`${inputCls} resize-none font-mono`}
              placeholder={'{\n  "title": "Monthly KPIs",\n  "subtitle": "Last 30 days"\n}'}
            />
            <p className={`text-[10px] ${colors.text.muted} mt-1`}>
              JSON metadata for the report header (title, subtitle, badges, etc.)
            </p>
            {headerError && (
              <p className="text-[10px] text-red-400 mt-1">{headerError}</p>
            )}
          </div>

          {/* Icon picker */}
          <div>
            <label className={labelCls}>Icon</label>
            <div className="grid grid-cols-6 gap-1">
              {REPORT_ICON_OPTIONS.map(({ value, label, Icon }) => (
                <button
                  key={value}
                  type="button"
                  title={label}
                  onClick={() => patch({ icon: value })}
                  className={`flex flex-col items-center gap-0.5 p-1.5 rounded-md border transition-colors ${
                    form.icon === value
                      ? 'border-indigo-500/60 bg-indigo-600/15 text-indigo-400'
                      : `${colors.border} ${colors.hover} ${colors.text.muted}`
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                  <span className="text-[8px] truncate w-full text-center">{label.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>

          <SettingToggleRow
            title="Published"
            description={
              form.ispublished ? 'Live — visible to end users' : 'Draft — not yet published'
            }
            checked={form.ispublished}
            onChange={(ispublished) => patch({ ispublished })}
            colors={colors}
            onClassName="bg-emerald-600"
          />

          <SettingToggleRow
            title="Active"
            description="Enable report generation & visibility"
            checked={form.isActive}
            onChange={(isActive) => patch({ isActive })}
            colors={colors}
          />

          {/* Source Type */}
          <div>
            <label className={labelCls}>Source Type</label>
            <select
              value={form.sourceType}
              onChange={(e) => patch({ sourceType: e.target.value as SourceType, workflowId: '', dataModelId: '' })}
              className={inputCls}
            >
              <option value="workflow">Workflow</option>
              <option value="datamodel">DataModel (Direct Execution)</option>
            </select>
          </div>

          {/* Conditional source selector */}
          {form.sourceType === 'workflow' && (
            <div>
              <label className={`${labelCls} flex items-center gap-1`}>
                <Workflow className="w-3 h-3" />
                Workflow
              </label>
              {loadingWorkflows ? (
                <div className={`${inputCls} flex items-center gap-2 ${colors.text.muted}`}>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Loading workflows...
                </div>
              ) : (
                <select
                  value={form.workflowId}
                  onChange={(e) => patch({ workflowId: e.target.value })}
                  className={inputCls}
                >
                  <option value="">Select workflow...</option>
                  {workflows.map((wf) => (
                    <option key={wf.id} value={wf.id}>
                      {wf.name}{wf.status ? ` · ${wf.status}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {form.sourceType === 'datamodel' && (
            <div>
              <label className={`${labelCls} flex items-center gap-1`}>
                <Database className="w-3 h-3" />
                Data Model
              </label>
              {loadingDataModels ? (
                <div className={`${inputCls} flex items-center gap-2 ${colors.text.muted}`}>
                  <RefreshCw className="w-3 h-3 animate-spin" />
                  Loading data models...
                </div>
              ) : (
                <select
                  value={form.dataModelId}
                  onChange={(e) => patch({ dataModelId: e.target.value })}
                  className={inputCls}
                >
                  <option value="">Select data model...</option>
                  {dataModels.map((dm) => (
                    <option key={dm.id} value={dm.id}>
                      {dm.name}
                    </option>
                  ))}
                </select>
              )}
              {form.dataModelId && (
                <p className={`text-[10px] ${colors.text.muted} mt-1`}>
                  {dataModels.find((d) => d.id === form.dataModelId)?.description || 'Direct execution against selected model'}
                </p>
              )}
            </div>
          )}

          {loadError && (
            <p className="text-[10px] text-red-400 px-2 py-1 rounded bg-red-500/10 border border-red-500/20">
              {loadError}
            </p>
          )}

          {/* Parameters & copilots */}
          <div className={`pt-2 border-t ${colors.border} space-y-2.5`}>
            <ReportParametersEditor
              parameters={form.parameters}
              onChange={(parameters) => patch({ parameters })}
              colors={colors}
              inputCls={inputCls}
              labelCls={labelCls}
            />
            {parameterError && (
              <p className="text-[10px] text-red-400">{parameterError}</p>
            )}
            {loadingCopilots ? (
              <div className={`flex items-center gap-2 text-[10px] ${colors.text.muted} py-1`}>
                <RefreshCw className="w-3 h-3 animate-spin" />
                Loading Copilots…
              </div>
            ) : (
              <FilterableMultiSelect
                label="Copilots"
                options={copilotOptions}
                selected={form.copilotIds}
                setSelected={(v) => patch({ copilotIds: v })}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={`flex-shrink-0 px-4 py-2.5 border-t ${colors.border} flex justify-end gap-2`}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className={`px-3 py-1.5 rounded-md text-[11px] ${colors.text.secondary} ${colors.hover} disabled:opacity-50`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={saving}
            className="px-3 py-1.5 rounded-md text-[11px] bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white font-medium"
          >
            {saving ? 'Saving…' : primaryActionLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportSettingsModal;
